import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Input, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { CopyOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { pairTradingApi, PairDetail, PairListItem } from '../../../services/pairTradingApi';
import { importInProgressOf } from '../../../services/importJobApi';
import ImportJobProgress from '../../../components/ImportJobProgress';
import { formatAbbrev, formatInt } from '../../../utils/numberFormat';
import { pairDisplayName, pairStatusLabel } from '../utils/pairSelection';

export interface PairLifecyclePanelProps {
    /** The selected saved pair; null on the New pair tab. */
    item: PairListItem | null;
    isNew: boolean;
    /** Name the New pair will be created with. */
    newPairName: string;
    onNewPairNameChange: (name: string) => void;
    /** Renamed — refresh the list. */
    onChanged: () => void;
    /** Ended (or, for a draft, discarded). */
    onEnded: (wasDraft: boolean) => void;
    /** Open a New pair tab prefilled from this pair. */
    onCopyToNew: () => void;
}

const GREY = '#848E9C';
const pnlColor = (n: number) => (n > 0 ? '#0ECB81' : n < 0 ? '#F6465D' : GREY);
const fmtDate = (iso: string | null | undefined) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '—');
const errorText = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

function Money({ value, abbrev }: { value: string | number | null | undefined; abbrev?: boolean }) {
    const n = Number(value ?? NaN);
    const exact = formatInt(n, { signed: n !== 0 });
    return (
        <span style={{ fontFamily: "'Courier New', monospace", color: pnlColor(n) }} title={abbrev ? exact : undefined}>
            {abbrev ? `${n > 0 ? '+' : ''}${formatAbbrev(n)}` : exact}
        </span>
    );
}

/** A leg-table amount: abbreviated (1.25B) so four columns fit a phone, exact in the tooltip. */
function LegAmount({ value }: { value: string | number | null | undefined }) {
    return <span title={formatInt(value)}>{formatAbbrev(value)}</span>;
}

/** "HNXDS:VN30F2612" → "VN30F2612": the exchange prefix only widens the column. */
const bareSymbol = (symbol: string) => symbol.split(':').pop() ?? symbol;

/** Settlement of an ENDED pair, from GET /pair/:dc/detail. */
export function PairSummaryCard({ detail }: { detail: PairDetail }) {
    const summary = detail.summary;
    if (!summary) {
        return <div style={{ color: GREY, fontSize: 11 }}>No settlement summary recorded for this pair.</div>;
    }
    // Each closed pair sits in exactly one of the two books.
    const closedCount = summary.closedPairs.reduce(
        (n, day) => n + (day.long?.length ?? 0) + (day.short?.length ?? 0),
        0,
    );
    const row = (label: string, value: React.ReactNode) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
            <span style={{ color: GREY, flexShrink: 0 }}>{label}</span>
            <span style={{ color: '#EAECEF', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
        </div>
    );
    return (
        <div
            data-testid="pair-summary-card"
            style={{ background: '#1E2329', border: '1px solid #2B3139', borderRadius: 4, padding: '8px 10px', marginBottom: 8, minWidth: 0, overflow: 'hidden' }}
        >
            <div style={{ fontWeight: 700, fontSize: 12, color: '#EAECEF', marginBottom: 6 }}>Settlement</div>
            {row('Opened', fmtDate(summary.firstOpenedAt ?? detail.firstOpenedAt))}
            {row('Ended', fmtDate(detail.endedAt))}
            {row('Reason', summary.endReason ?? detail.endReason ?? '—')}
            {row('Closed pairs', formatInt(closedCount))}
            {row('Settled (VND)', <span style={{ fontFamily: "'Courier New', monospace" }}>{formatInt(summary.settled)}</span>)}
            {row('Realized PnL (VND)', <Money value={summary.realizedPnl} />)}
            {summary.legs.length > 0 && (
                // Fixed layout at the card's width: a long symbol wraps inside its cell
                // instead of pushing the amounts past the card's edge.
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 11, marginTop: 6 }}>
                    <thead>
                        <tr style={{ color: GREY }}>
                            <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 600, width: '34%' }}>Leg</th>
                            <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 600 }}>Deposited</th>
                            <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 600 }}>Settled</th>
                            <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 600 }}>PnL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.legs.map((leg) => (
                            <tr key={`${leg.leg}:${leg.symbol}`} style={{ borderTop: '1px solid #2B3139' }}>
                                <td style={{ padding: '2px 4px', color: '#EAECEF', overflowWrap: 'anywhere' }} title={leg.symbol}>
                                    {bareSymbol(leg.symbol)}
                                    <div style={{ color: GREY, fontSize: 10 }}>{leg.leg === 'DERIV' ? 'futures' : 'stock'}</div>
                                </td>
                                <td style={{ padding: '2px 4px', textAlign: 'right', color: '#EAECEF' }}><LegAmount value={leg.deposited} /></td>
                                <td style={{ padding: '2px 4px', textAlign: 'right', color: '#EAECEF' }}><LegAmount value={leg.settled} /></td>
                                <td style={{ padding: '2px 4px', textAlign: 'right' }}><Money value={leg.realizedPnl} abbrev /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

/**
 * Header of the config column for the selected pair: its name (rename inline), lifecycle
 * status, End pair, and — once it has ended — the settlement summary and Copy to new pair.
 */
const PairLifecyclePanel: React.FC<PairLifecyclePanelProps> = ({
    item,
    isNew,
    newPairName,
    onNewPairNameChange,
    onChanged,
    onEnded,
    onCopyToNew,
}) => {
    const { modal, message } = App.useApp();
    const [detail, setDetail] = useState<PairDetail | null>(null);
    const [ending, setEnding] = useState(false);
    // End refused because a leg still holds: offer End from broker here, as a grid symbol does
    const [brokerEnd, setBrokerEnd] = useState<{ reason: string; importId: string | null; note: string | null } | null>(null);
    const [brokerEndConfirm, setBrokerEndConfirm] = useState('');
    const [brokerEnding, setBrokerEnding] = useState(false);

    const dataConfigId = item?.dataConfigId ?? null;
    const status = item?.lifecycleStatus ?? null;

    useEffect(() => {
        setDetail(null);
        if (!dataConfigId || status !== 'ENDED') return;
        let cancelled = false;
        pairTradingApi
            .getPairDetail(dataConfigId)
            .then((d) => { if (!cancelled) setDetail(d); })
            .catch(() => { if (!cancelled) setDetail(null); });
        return () => { cancelled = true; };
    }, [dataConfigId, status]);

    if (isNew) {
        return (
            <div style={{ marginTop: 8, marginBottom: 8 }} data-testid="pair-lifecycle-new">
                <Input
                    size="small"
                    placeholder="Pair name (optional)"
                    maxLength={80}
                    value={newPairName}
                    onChange={(e) => onNewPairNameChange(e.target.value)}
                />
                <div style={{ color: GREY, fontSize: 11, marginTop: 4 }}>
                    New pair — not saved yet. Save creates it as a draft; then Deposit and Start.
                </div>
            </div>
        );
    }
    if (!item) return null;

    const rename = async (next: string) => {
        const name = next.trim();
        if (!name || name === (item.name ?? pairDisplayName(item))) return;
        try {
            await pairTradingApi.renamePair(item.dataConfigId, name);
            onChanged();
        } catch (e: any) {
            message.error(errorText(e, 'Rename failed'));
        }
    };

    const canEnd = status === 'DRAFT' || status === 'ACTIVE';
    const confirmEnd = () => {
        modal.confirm({
            title: 'End this pair?',
            content: 'Its cash returns to the account and the config becomes read-only history.',
            okText: 'End pair',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                setEnding(true);
                try {
                    const res = await pairTradingApi.endPair(item.dataConfigId);
                    const withdrawn = Number(res?.withdrawn ?? 0);
                    message.success(
                        withdrawn > 0
                            ? `Pair ended — ${formatInt(withdrawn)} VND returned to the account.`
                            : status === 'DRAFT' ? 'Draft discarded.' : 'Pair ended.',
                    );
                    onEnded(status === 'DRAFT');
                } catch (e: any) {
                    const reason = errorText(e, 'Failed to end the pair');
                    if (e?.response?.status === 409 && /still holds/.test(reason)) {
                        setBrokerEndConfirm('');
                        setBrokerEnd({ reason, importId: null, note: null });
                    } else {
                        message.error(reason);
                    }
                } finally {
                    setEnding(false);
                }
            },
        });
    };

    const endFromBroker = async () => {
        setBrokerEnding(true);
        try {
            const { importId } = await pairTradingApi.endPairFromBroker(item.dataConfigId, brokerEndConfirm.trim());
            setBrokerEnd((prev) => prev && { ...prev, importId });
        } catch (e: any) {
            const open409 = importInProgressOf(e);
            if (open409) setBrokerEnd((prev) => prev && { ...prev, importId: open409.importId, note: open409.message });
            else message.error(errorText(e, 'Failed to end the pair'));
        } finally {
            setBrokerEnding(false);
        }
    };

    return (
        <div style={{ marginTop: 8, marginBottom: 8 }} data-testid="pair-lifecycle-panel">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Typography.Text
                    strong
                    style={{ color: '#EAECEF', fontSize: 13, maxWidth: 260 }}
                    ellipsis={{ tooltip: true }}
                    editable={{
                        tooltip: 'Rename',
                        maxLength: 80,
                        text: item.name ?? pairDisplayName(item),
                        onChange: (v) => { void rename(v); },
                    }}
                >
                    {pairDisplayName(item)}
                </Typography.Text>
                <Tag style={{ marginInlineEnd: 0 }}>{pairStatusLabel(item)}</Tag>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                    {(status === 'ENDED' || status === 'ENDING') && (
                        <Tooltip title="Open a new pair with these settings. Reference prices are left empty — enter them again.">
                            <Button size="small" icon={<CopyOutlined />} onClick={onCopyToNew} data-testid="pair-copy-to-new">
                                Copy to new pair
                            </Button>
                        </Tooltip>
                    )}
                    {canEnd && (
                        <Tooltip
                            title={item.running
                                ? 'Stop the bot first.'
                                : status === 'DRAFT'
                                    ? 'Discard this draft and return any cash to the account.'
                                    : 'End now: needs the bot stopped. With legs still open you can end it without selling.'}
                        >
                            <Button
                                size="small"
                                danger
                                icon={<StopOutlined />}
                                loading={ending}
                                disabled={item.running}
                                onClick={confirmEnd}
                                data-testid="pair-end-button"
                            >
                                End pair
                            </Button>
                        </Tooltip>
                    )}
                </span>
            </div>
            <div style={{ color: GREY, fontSize: 11, marginTop: 2, overflowWrap: 'anywhere' }}>
                {item.firstOpenedAt ? `Opened ${fmtDate(item.firstOpenedAt)}` : 'Never opened'}
                {' · '}Capital {formatInt(item.totalCapital)} VND
                {status !== 'DRAFT' && (
                    <>
                        {' · '}Realized <Money value={item.realizedPnl} />
                    </>
                )}
            </div>
            <Modal
                open={!!brokerEnd}
                title="End the pair without selling?"
                onCancel={() => setBrokerEnd(null)}
                footer={null}
                destroyOnClose
            >
                {brokerEnd?.importId ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {brokerEnd.note && <Alert type="warning" showIcon message={brokerEnd.note} />}
                        <ImportJobProgress
                            importId={brokerEnd.importId}
                            onReady={() => {
                                message.success('Pair ended. What the broker still holds now shows in the basic book.');
                                setBrokerEnd(null);
                                onEnded(false);
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                        <Alert type="warning" showIcon message={brokerEnd?.reason} />
                        <span>
                            Nothing is sold: use this when the bot cannot close the pair itself (e.g. an order stuck
                            at the broker). Every leg is archived and what each was given returns to the account's free
                            cash. Shares and contracts the broker still holds belong to no bot afterwards: close them on
                            the price board.
                        </span>
                        <Space wrap>
                            <Input
                                value={brokerEndConfirm}
                                onChange={(e) => setBrokerEndConfirm(e.target.value)}
                                placeholder="Type END to confirm"
                                style={{ width: 180 }}
                            />
                            <Button
                                danger
                                loading={brokerEnding}
                                disabled={brokerEndConfirm.trim() !== 'END'}
                                onClick={() => void endFromBroker()}
                                data-testid="pair-end-from-broker"
                            >
                                End without selling
                            </Button>
                        </Space>
                    </div>
                )}
            </Modal>
            {status === 'ENDING' && (
                <Alert
                    style={{ marginTop: 6 }}
                    type="info"
                    showIcon
                    message="Every pair has closed — settling and returning cash to the account."
                />
            )}
            {status === 'ENDED' && (
                <>
                    <Alert
                        style={{ marginTop: 6, marginBottom: 6 }}
                        type="warning"
                        showIcon
                        message="This pair has ended and is read-only. Create a new pair or copy this one."
                    />
                    {detail && <PairSummaryCard detail={detail} />}
                </>
            )}
        </div>
    );
};

export default PairLifecyclePanel;
